import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useApi, img, SITE_URL } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Seo } from "@/components/site/Seo";
import { Reveal, ZoomImg } from "@/components/site/Reveal";
import { RichText } from "@/components/site/RichText";
import { StoryCard } from "@/components/site/StoryCard";
import NotFound from "@/pages/NotFound";

const ytId = (url) => url?.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/)?.[1];

const Media = ({ p, title }) => {
  const vid = ytId(p.video_url);
  if (vid) return <div className="aspect-video overflow-hidden rounded-2xl" data-testid="story-youtube"><iframe className="h-full w-full" src={`https://www.youtube.com/embed/${vid}`} title={title} allowFullScreen loading="lazy" /></div>;
  if (p.video_file) return <video src={img(p.video_file)} poster={img(p.cover_image)} controls playsInline className="w-full rounded-2xl bg-ink" data-testid="story-video" />;
  if (p.cover_image) return <ZoomImg src={img(p.cover_image)} alt={title} className="max-h-[70vh] w-full rounded-2xl object-cover" data-testid="story-cover" />;
  return null;
};

export default function StoryDetail() {
  const { slug } = useParams();
  const { t, L, to, lang } = useLang();
  const { data: p, loading, error } = useApi(`/posts/${slug}`);
  const { data: all } = useApi("/posts?limit=4", []);
  if (error) return <NotFound />;
  if (loading || !p) return <div className="container-x py-32 text-moss" data-testid="story-loading">{t("common.loading")}</div>;
  const title = L(p, "title");
  const more = all.filter((x) => x.slug !== p.slug).slice(0, 3);
  const date = p.published_at && new Date(p.published_at.length === 10 ? `${p.published_at}T12:00:00` : p.published_at).toLocaleDateString(lang === "es" ? "es-NI" : "en-US", { year: "numeric", month: "long", day: "numeric" });
  const jsonLd = {
    "@context": "https://schema.org", "@type": "BlogPosting", headline: title, description: L(p, "seo_description") || L(p, "excerpt"),
    image: p.cover_image ? (p.cover_image.startsWith("/media") ? SITE_URL + p.cover_image : p.cover_image) : undefined,
    author: { "@type": "Organization", name: p.author || "Escamequita" }, inLanguage: lang, keywords: p.keywords.join(", "),
    ...(p.published_at ? { datePublished: p.published_at } : {}), mainEntityOfPage: `${SITE_URL}${to(`/stories/${p.slug}`)}`,
  };
  return (
    <>
      <Seo title={L(p, "seo_title") || `${title} | Escamequita`} description={L(p, "seo_description") || L(p, "excerpt")} image={p.cover_image} jsonLd={jsonLd} />
      <article className="container-x pt-10" data-testid="story-detail">
        <Link to={to("/stories")} className="inline-flex items-center gap-1 text-sm font-bold text-moss hover:text-forest" data-testid="story-back-link"><ChevronLeft className="h-4 w-4" /> {t("stories.back")}</Link>
        <Reveal className="mx-auto mt-6 max-w-3xl">
          {date && <p className="eyebrow">{date}</p>}
          <h1 className="mt-3 text-4xl font-black leading-[1.05] tracking-tight text-forest sm:text-5xl lg:text-6xl" data-testid="story-title">{title}</h1>
          <p className="mt-6 text-lg font-semibold leading-relaxed text-ink">{L(p, "excerpt")}</p>
        </Reveal>
        <div className="mx-auto mt-10 max-w-5xl"><Media p={p} title={title} /></div>
        <div className="mx-auto max-w-3xl py-14">
          <RichText text={L(p, "content")} />
          {p.keywords.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2" data-testid="story-keywords">
              {p.keywords.map((k) => <span key={k} className="rounded-full bg-linen px-3 py-1 text-xs font-bold text-moss">#{k}</span>)}
            </div>
          )}
        </div>
      </article>
      {more.length > 0 && (
        <section className="border-t border-clay bg-linen py-20" data-testid="story-more">
          <div className="container-x">
            <h2 className="text-3xl font-black text-forest sm:text-4xl">{t("stories.more")}</h2>
            <div className="mt-10 grid gap-10 md:grid-cols-3">{more.map((x) => <StoryCard key={x.slug} post={x} />)}</div>
          </div>
        </section>
      )}
    </>
  );
}
